import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import '../utils/sessionGuard'; // Must be first — wraps fetch/XHR before any requests
import React, { Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ErrorBoundary from '../components/common/ErrorBoundary';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SessionExpiredModal from '../components/common/SessionExpiredModal';
import { ThemeProvider } from '../features/home/context/ThemeContext';
import { initializeMapRoot, handleMapRouteChange } from '../features/home/map-root';
import '../styles/globals.css';
// Feature imports - all lazy loaded from features/
const AdminApp = React.lazy(() => import('../features/admin'));
const UprnDatasetsApp = React.lazy(() => import('../features/uprn-datasets'));
const OpenUprnApp = React.lazy(() => import('../features/open-uprn'));
const AreaApp = React.lazy(() => import('../features/area'));
const PonMonApp = React.lazy(() => import('../features/ponmon'));
const LosApp = React.lazy(() => import('../features/los'));
const VoucherApp = React.lazy(() => import('../features/voucher'));
const OrderApp = React.lazy(() => import('../features/order'));
const PiaNoiApp = React.lazy(() => import('../features/pia-noi'));
const AuthApp = React.lazy(() => import('../features/auth'));
const InfoApp = React.lazy(() => import('../features/info'));
const InstallsApp = React.lazy(() => import('../features/installs'));
const AggregationNodeApp = React.lazy(() => import('../features/aggregation-node'));
const InfrastructureApp = React.lazy(() => import('../features/infrastructure'));
const UprnToolApp = React.lazy(() => import('../features/uprn-tool'));
const MyUploadsApp = React.lazy(() => import('../features/my-uploads'));
const RiskAssessmentApp = React.lazy(() => import('../features/risk-assessment'));
const SafetyCodeApp = React.lazy(() => import('../features/safety-code'));
const VehicleChecklistApp = React.lazy(() => import('../features/vehicle-checklist'));
const OverdueNotification = React.lazy(() => import('../features/vehicle-checklist/components/OverdueNotification'));
const UnauthorisedPage = React.lazy(() => import('../pages/error/UnauthorisedPage'));
/**
 * Hook to initialize and manage the separate map root.
 * The map is rendered in a completely separate React root to prevent
 * React 19's Suspense behavior from unmounting it during route transitions.
 */
const useMapRoot = (isAuthenticated) => {
    const location = useLocation();
    useEffect(() => {
        if (isAuthenticated) {
            // Initialize the map root (only happens once)
            initializeMapRoot();
        }
    }, [isAuthenticated]);
    useEffect(() => {
        // Update map visibility when route changes
        handleMapRouteChange(location.pathname);
    }, [location.pathname]);
};
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 5 * 60 * 1000, // 5 minutes
        }
    }
});
const LoadingState = () => (_jsx("div", { className: "flex min-h-screen items-center justify-center", children: _jsx(LoadingSpinner, { size: "lg" }) }));
/**
 * Inner component that handles routes and manages the map root.
 * Must be inside BrowserRouter for useLocation to work.
 */
const AppRoutes = ({ isAuthenticated, user, groups, sudoOriginalUser, authMessages, authEmail, authResetToken }) => {
    // Initialize and manage the separate map root (rendered outside React Router tree)
    useMapRoot(isAuthenticated);
    const requireAuth = (element) => {
        if (!isAuthenticated) {
            return _jsx(Navigate, { to: "/users/login", replace: true });
        }
        return element;
    };
    return (_jsx(Suspense, { fallback: _jsx(LoadingState, {}), children: _jsxs(Routes, { children: [_jsx(Route, { path: "/admin/*", element: requireAuth(_jsx(AdminApp, {})) }), _jsx(Route, { path: "/uprn-datasets/*", element: requireAuth(_jsx(UprnDatasetsApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/open-uprn/*", element: requireAuth(_jsx(OpenUprnApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/area/*", element: requireAuth(_jsx(AreaApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/voucher/*", element: requireAuth(_jsx(VoucherApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/aggregation-node/*", element: requireAuth(_jsx(AggregationNodeApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/infrastructure/*", element: requireAuth(_jsx(InfrastructureApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/order/*", element: requireAuth(_jsx(OrderApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/info/*", element: requireAuth(_jsx(InfoApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/installs/*", element: requireAuth(_jsx(InstallsApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/ponmon/*", element: requireAuth(_jsx(PonMonApp, {})) }), _jsx(Route, { path: "/los/*", element: requireAuth(_jsx(LosApp, {})) }), _jsx(Route, { path: "/pia_noi/*", element: requireAuth(_jsx(PiaNoiApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/users/*", element: _jsx(AuthApp, { messages: authMessages, email: authEmail, resetToken: authResetToken }) }), _jsx(Route, { path: "/uprn/*", element: requireAuth(_jsx(UprnToolApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/my-uploads/*", element: requireAuth(_jsx(MyUploadsApp, {})) }), _jsx(Route, { path: "/risk/*", element: requireAuth(_jsx(RiskAssessmentApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/safety-code/*", element: requireAuth(_jsx(SafetyCodeApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/vehicles/*", element: requireAuth(_jsx(VehicleChecklistApp, { user: user, groups: groups, sudoOriginalUser: sudoOriginalUser })) }), _jsx(Route, { path: "/unauthorised", element: _jsx(UnauthorisedPage, {}) }), _jsx(Route, { path: "/", element: isAuthenticated
                        ? null // Map is rendered by the separate map root, not here
                        : _jsx(Navigate, { to: "/users/login", replace: true }) }), _jsx(Route, { path: "*", element: isAuthenticated
                        ? _jsx(Navigate, { to: "/", replace: true })
                        : _jsx(Navigate, { to: "/users/login", replace: true }) })] }) }));
};
const AppShell = () => {
    const cfn = window.CFN || {};
    const user = cfn.userId
        ? {
            id: cfn.userId,
            name: cfn.userName,
            email: cfn.userEmail
        }
        : null;
    const groups = Array.isArray(cfn.userGroups) ? cfn.userGroups : [];
    const sudoOriginalUser = cfn.sudoOriginalUser || null;
    const authMessages = cfn.messages || {};
    const authEmail = cfn.email || '';
    const authResetToken = cfn.resetToken || '';
    const isAuthenticated = Boolean(user);
    return (_jsx(ErrorBoundary, { children: _jsx(ThemeProvider, { defaultTheme: "dark", children: _jsxs(QueryClientProvider, { client: queryClient, children: [_jsxs(BrowserRouter, { children: [_jsx(AppRoutes, { isAuthenticated: isAuthenticated, user: user, groups: groups, sudoOriginalUser: sudoOriginalUser, authMessages: authMessages, authEmail: authEmail, authResetToken: authResetToken }), isAuthenticated && window.CFN?.hasVehicleAssignments && (_jsx(Suspense, { fallback: null, children: _jsx(OverdueNotification, {}) }))] }), _jsx(SessionExpiredModal, {})] }) }) }));
};
const rootElement = document.getElementById('app-root');
if (rootElement) {
    const root = createRoot(rootElement);
    root.render(_jsx(AppShell, {}));
}
